import { Camera } from "lucide-react";
import CompareSlider from "@/components/CompareSlider";
import { PageHero, SectionHeading, Reveal, CTABand, usePageMeta } from "@/components/kit";
import { GALLERY, COMPARE_PAIRS, HERO_BG, SLOGAN } from "@/lib/site";

export default function GalleryPage() {
  usePageMeta(
    "Project Gallery | Redeemer Solutions LLC",
    "See security camera, access control, fiber optic, Ethernet, and video intercom service work by Redeemer Solutions LLC across Middle Tennessee — including before & after clean-ups."
  );
  return (
    <div data-testid="gallery-page">
      <PageHero
        testid="gallery-hero"
        eyebrow="Project Gallery"
        lines={["Work We're", { t: "Proud to Sign", gold: true }]}
        sub="A look at recent service calls — faults found fast, cable dressed clean, and systems restored to serve for years. New photos are added as jobs complete."
        image={HERO_BG}
        imageAlt="Completed security technology service work"
      />

      <section data-testid="before-after-section" className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 sm:pt-24">
        <SectionHeading
          eyebrow="Before & After"
          title="Drag the line. See the difference."
          sub="A real network cabinet and data wall clean-up at Calvary Baptist Church in Shelbyville — drag each gold handle to compare the mess we found with the order we left behind."
          testid="before-after-heading"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {COMPARE_PAIRS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <CompareSlider before={p.before} after={p.after} title={p.title} testid={`compare-${p.id}`} />
              <div className="mt-4 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1">
                <h3 className="font-display text-lg font-semibold text-[#F4EFE6]">{p.title}</h3>
                <p className="text-xs text-zinc-500">{p.location}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <SectionHeading eyebrow="From the Field" title="More recent work" testid="gallery-more-heading" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {GALLERY.map((g, i) => (
            <Reveal key={g.title} delay={(i % 3) * 0.08}>
              <figure
                data-testid={`gallery-item-${i}`}
                className="group relative overflow-hidden rounded-sm border border-gold/15 gold-glow-card"
              >
                <img
                  src={g.url}
                  alt={`${g.title}${g.location ? ` — ${g.location}` : ""}`}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover opacity-85 transition-transform duration-700 group-hover:scale-105"
                />
                {g.badge && (
                  <span data-testid={`gallery-badge-${i}`} className="absolute top-4 left-4 font-mono-label text-[10px] uppercase tracking-[0.2em] bg-[#0A0A0C]/85 border border-gold/50 text-gold px-3 py-1.5 rounded-sm backdrop-blur-sm">
                    {g.badge}
                  </span>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/95 via-[#0A0A0C]/20 to-transparent" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-mono-label text-[10px] uppercase tracking-[0.24em] text-gold">{g.category}</p>
                  <h3 className="font-display text-base font-semibold text-[#F4EFE6] mt-1.5">{g.title}</h3>
                  {g.location && <p className="text-xs text-zinc-400 mt-1">{g.location}</p>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14">
          <div data-testid="gallery-note" className="flex flex-col sm:flex-row items-center justify-center gap-4 border border-gold/20 bg-[#0D0D10] rounded-sm px-8 py-7 text-center sm:text-left">
            <Camera size={22} className="text-gold shrink-0" />
            <p className="text-zinc-400 text-sm leading-relaxed">
              We photograph select service calls with client permission. Dealing with a similar issue? Call us —
              we're glad to share references from churches, schools, and businesses near you.
            </p>
          </div>
        </Reveal>
      </section>

      <CTABand title={SLOGAN} sub="Your restored system could be the next before & after on this page. Request a quote today." />
    </div>
  );
}
